import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Careers() {
  useEffect(() => {
    // Update page title and meta description
    document.title = '求人情報 - おかやまリフォーム株式会社 | 岡山市・倉敷市のリフォーム業界で働く';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '岡山市・倉敷市のリフォーム業界で働きませんか？おかやまリフォーム株式会社では施工スタッフ、現場監督、営業スタッフを募集中。充実した研修制度と福利厚生で安心して働けます。');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '求人情報,採用,リフォーム業界,岡山市求人,倉敷市求人,建設業求人,施工管理');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/careers`);
    }

    // Schema.org JSON-LD for JobPosting
    const jobPostingSchema = {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": "リフォーム施工スタッフ",
      "description": "住宅リフォーム工事の施工業務を担当していただきます。キッチン、バスルーム、内装工事など幅広い工事に携わることができます。",
      "hiringOrganization": {
        "@type": "Organization",
        "name": "おかやまリフォーム株式会社",
        "sameAs": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "岡山市",
          "addressRegion": "岡山県",
          "addressCountry": "JP"
        }
      },
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "JPY",
        "value": {
          "@type": "QuantitativeValue",
          "minValue": 250000,
          "maxValue": 400000,
          "unitText": "MONTH"
        }
      },
      "employmentType": "FULL_TIME",
      "datePosted": "2024-12-19"
    };

    const existingScript = document.getElementById('job-posting-schema');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'job-posting-schema';
    script.textContent = JSON.stringify(jobPostingSchema);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.getElementById('job-posting-schema');
      if (schemaScript) schemaScript.remove();
    };
  }, []);

  const jobOpenings = [
    {
      title: 'リフォーム施工スタッフ',
      type: '正社員',
      experience: '経験者優遇（未経験者も歓迎）',
      salary: '月給25万円〜40万円',
      description: '住宅リフォーム工事の施工業務を担当していただきます。キッチン、バスルーム、内装工事など幅広い工事に携わることができます。',
      requirements: [
        '普通自動車免許（AT限定可）',
        '建設業界での経験があれば尚良い',
        'チームワークを大切にできる方',
        '岡山市・倉敷市近郊にお住まいの方'
      ]
    },
    {
      title: '現場監督・施工管理',
      type: '正社員',
      experience: '施工管理経験3年以上',
      salary: '月給30万円〜50万円',
      description: 'リフォーム工事の現場管理、品質管理、安全管理を担当していただきます。お客様との打ち合わせや工程管理も重要な業務です。',
      requirements: [
        '施工管理技士資格（1級・2級問わず）',
        '建設業界での現場管理経験',
        'コミュニケーション能力',
        'PC操作（Word、Excel）'
      ]
    },
    {
      title: '営業・設計スタッフ',
      type: '正社員',
      experience: '営業経験者優遇',
      salary: '月給22万円〜35万円＋歩合',
      description: 'お客様への提案営業、現地調査、見積もり作成、設計業務を担当していただきます。お客様の理想を形にするやりがいのある仕事です。',
      requirements: [
        '普通自動車免許',
        '営業経験または接客経験',
        'CADスキルがあれば尚良い',
        'お客様とのコミュニケーションを大切にできる方'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative h-64 sm:h-80 md:h-96 bg-cover bg-center" style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/dfdc965ab3571467a1efc3a5ac7dfccb.jpg')`
        }}>
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
                求人情報
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-white/90">
                一緒に岡山県の住環境を支える<br className="sm:hidden" />仲間を募集しています
              </p>
            </div>
          </div>
        </section>

        {/* Company Culture */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                  <strong>働きやすい環境づくり</strong>
                </h2>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">
                  おかやまリフォーム株式会社では、スタッフ一人ひとりが働きやすい環境づくりを大切にしています。岡山市・倉敷市を中心とした地域密着型の企業として、地元で長く働ける職場を目指しています。
                </p>
                <div className="space-y-3 sm:space-y-4 text-sm sm:text-base">
                  <div className="flex items-start">
                    <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                    <span className="text-gray-700">充実した研修制度で未経験者もサポート</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                    <span className="text-gray-700">資格取得支援制度あり</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                    <span className="text-gray-700">完全週休2日制（日曜・祝日）</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                    <span className="text-gray-700">各種社会保険完備</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                    <span className="text-gray-700">昇給・賞与あり</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20construction%20team%20meeting%2C%20workers%20discussing%20project%20plans%2C%20collaborative%20work%20environment%2C%20safety%20meeting%2C%20teamwork%2C%20modern%20office%20setting%2C%20positive%20workplace%20culture&width=600&height=400&seq=careers-culture&orientation=landscape"
                  alt="職場環境"
                  className="w-full h-auto rounded-lg shadow-lg object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Job Openings */}
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8 sm:mb-12">
              <strong>募集職種</strong>
            </h2>
            <div className="space-y-6 sm:space-y-8">
              {jobOpenings.map((job, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="p-6 sm:p-8">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4 sm:mb-6">
                      <div className="mb-4 lg:mb-0">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">{job.title}</h3>
                        <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
                          <span className="bg-teal-100 text-teal-800 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                            {job.type}
                          </span>
                          <span className="bg-green-100 text-green-800 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                            {job.experience}
                          </span>
                        </div>
                      </div>
                      <div className="lg:text-right">
                        <p className="text-xl sm:text-2xl font-bold text-teal-600">{job.salary}</p>
                      </div>
                    </div>
                    
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 sm:mb-6">{job.description}</p>
                    
                    <div>
                      <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">応募資格・条件</h4>
                      <ul className="space-y-2">
                        {job.requirements.map((req, reqIndex) => (
                          <li key={reqIndex} className="flex items-start text-sm sm:text-base">
                            <i className="ri-arrow-right-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                            <span className="text-gray-700">{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8 sm:mb-12">
              <strong>福利厚生・待遇</strong>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              <div className="bg-gray-50 rounded-lg p-5 sm:p-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                  <i className="ri-calendar-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">休日・休暇</h3>
                <ul className="space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-700">
                  <li>完全週休2日制（日曜・祝日）</li>
                  <li>年次有給休暇</li>
                  <li>夏季休暇</li>
                  <li>年末年始休暇</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-5 sm:p-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                  <i className="ri-shield-check-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">保険・年金</h3>
                <ul className="space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-700">
                  <li>健康保険</li>
                  <li>厚生年金</li>
                  <li>雇用保険</li>
                  <li>労災保険</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-5 sm:p-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                  <i className="ri-graduation-cap-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">教育・研修</h3>
                <ul className="space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-700">
                  <li>新人研修制度</li>
                  <li>技術研修</li>
                  <li>資格取得支援</li>
                  <li>外部セミナー参加</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-5 sm:p-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                  <i className="ri-money-dollar-circle-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">給与・手当</h3>
                <ul className="space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-700">
                  <li>昇給年1回</li>
                  <li>賞与年2回</li>
                  <li>交通費支給</li>
                  <li>資格手当</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-5 sm:p-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                  <i className="ri-car-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">その他</h3>
                <ul className="space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-700">
                  <li>社用車貸与</li>
                  <li>制服貸与</li>
                  <li>工具支給</li>
                  <li>退職金制度</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-5 sm:p-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                  <i className="ri-team-line text-xl sm:text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">職場環境</h3>
                <ul className="space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-700">
                  <li>アットホームな職場</li>
                  <li>風通しの良い環境</li>
                  <li>チームワーク重視</li>
                  <li>地域密着型企業</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Application */}
        <section className="py-12 sm:py-16 bg-teal-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
              <strong>応募・お問い合わせ</strong>
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-6 sm:mb-8">
              ご応募やご質問がございましたら、お電話またはお問い合わせフォームからお気軽にご連絡ください。<br />
              まずは職場見学からでも大歓迎です。
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <a
                href="tel:086-012-3456"
                className="bg-teal-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                お電話でのお問い合わせ
              </a>
              <a
                href="/#contact"
                className="bg-white text-teal-600 border border-teal-600 px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                お問い合わせフォーム
              </a>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mt-4 sm:mt-6">
              電話受付時間：9:00〜18:00（日曜・祝日除く）
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
